import React, { Suspense } from 'react';
import { BrowserRouter, Outlet, Route, Routes } from 'react-router-dom';

import Layout from 'components/Layout/Layout';
import Loader from 'components/Loader';
import { isAlphaPopupWindow } from 'components/Alpha/alphaVoicePopup';
import { Integrations } from 'constants/integrations';

import Login from 'pages/auth/Login';
import DuoLogin from 'pages/auth/DuoLogin';
import ExternalLogin from 'pages/auth/ExternalLogin';

import {
  AUTOTASK_POD,
  AUTOTASK_POD_FULL_SIZE,
  AUTOTASK_POD_WIDGET,
  CONNECTWISE_POD,
  DUO_LOGIN,
  EXTERNAL_LOGIN,
  FRESH_SERVICE_FULL_SIZE,
  FRESH_SERVICE_POD,
  FRESH_SERVICE_POD_WIDGET,
  // FRESH_SERVICE_POD_FULL_SIZE,
  HALO_POD,
  IMPORT_INTEGRATION_POD,
  KASEYA_POD,
  MICROSOFT_365_POD,
  SUPER_OPS_POD,
  SERVICE_NOW_POD,
  SYNCRO_POD,
  TEAMS_SECURE_ATTACHMENT,
  TEAMS_SECURE_ATTACHMENT_AUTH_START,
  TEAMS_SECURE_ATTACHMENT_AUTH_END,
  ZENDESK_POD,
  WEB_EXTENSION_POD,
} from 'router/routes';

const AlphaPopupPage = React.lazy(() => import('components/Alpha/AlphaPopupPage'));
const AutotaskPod = React.lazy(() => import('pages/autotask'));
const ConnectwisePod = React.lazy(() => import('pages/connectwise'));
const HaloPod = React.lazy(() => import('pages/halo'));
const FreshServicePod = React.lazy(() => import('pages/freshService/FreshServicePod'));
const FullSizeAutotask = React.lazy(() => import('pages/fullSizeAutotask'));
const FullSizefreshService = React.lazy(() => import('pages/FullSizeFreshService'));
const KaseyaPod = React.lazy(() => import('pages/kaseya'));
const SyncroPod = React.lazy(() => import('pages/syncro'));
const SuperOpsPod = React.lazy(() => import('pages/superOps'));
const ServiceNowPod = React.lazy(() => import('pages/serviceNow'));
const ZendeskPod = React.lazy(() => import('pages/zendesk'));
const MicrosoftPod = React.lazy(() => import('pages/microsoft/MicrosoftPOD'));
const WebExtensionPod = React.lazy(() => import('pages/webExtension/WebExtensionPod'));
const ImportIntegrationPod = React.lazy(
  () => import('pages/importIntegration/ImportIntegrationPod'),
);
const SecureAttachmentDialog = React.lazy(() => import('pages/teams/SecureAttachmentDialog'));
const SecureAttachmentAuthStart = React.lazy(() => import('pages/teams/SecureAttachmentAuthStart'));
const SecureAttachmentAuthEnd = React.lazy(() => import('pages/teams/SecureAttachmentAuthEnd'));

const POPUP_ROUTES: Array<[string, Integrations]> = [
  [CONNECTWISE_POD, Integrations.ConnectWise],
  [HALO_POD, Integrations.Halo],
  [KASEYA_POD, Integrations.Kaseya],
  [SYNCRO_POD, Integrations.Syncro],
  [SUPER_OPS_POD, Integrations.SuperOps],
  [ZENDESK_POD, Integrations.Zendesk],
  [AUTOTASK_POD, Integrations.Autotask],
  [AUTOTASK_POD_WIDGET, Integrations.Autotask],
  [FRESH_SERVICE_POD, Integrations.FreshService],
  [FRESH_SERVICE_POD_WIDGET, Integrations.FreshService],
  [MICROSOFT_365_POD, Integrations.Microsoft],
  [SERVICE_NOW_POD, Integrations.ServiceNow],
  [IMPORT_INTEGRATION_POD, Integrations.ImportIntegration],
];

const AlphaPopupRouter: React.FC = () => (
  <BrowserRouter>
    <Routes>
      <Route path={'/login'} element={<Login />} />
      {POPUP_ROUTES.map(([path, psa]) => (
        <Route
          key={path}
          path={path}
          element={
            <Suspense fallback={<Loader />}>
              <AlphaPopupPage psa={psa} />
            </Suspense>
          }
        />
      ))}
      <Route path={'/*'} element={<Login />} />
    </Routes>
  </BrowserRouter>
);

const Router: React.FC = () => {
  if (isAlphaPopupWindow()) return <AlphaPopupRouter />;

  return (
    <BrowserRouter>
      <Routes>
        <Route path={'/login'} element={<Login />} />
        <Route path={DUO_LOGIN} element={<DuoLogin />} />
        <Route path={EXTERNAL_LOGIN} element={<ExternalLogin />} />
        <Route
          path={TEAMS_SECURE_ATTACHMENT}
          element={
            <Suspense fallback={<Loader />}>
              <SecureAttachmentDialog />
            </Suspense>
          }
        />
        <Route
          path={TEAMS_SECURE_ATTACHMENT_AUTH_START}
          element={
            <Suspense fallback={<Loader />}>
              <SecureAttachmentAuthStart />
            </Suspense>
          }
        />
        <Route
          path={TEAMS_SECURE_ATTACHMENT_AUTH_END}
          element={
            <Suspense fallback={<Loader />}>
              <SecureAttachmentAuthEnd />
            </Suspense>
          }
        />

        <Route
          path="/pod"
          element={
            <Suspense fallback={<Loader />}>
              <Layout>
                <Outlet />
              </Layout>
            </Suspense>
          }
        >
          <Route path={CONNECTWISE_POD} element={<ConnectwisePod />} />
          <Route path={HALO_POD} element={<HaloPod />} />
          <Route path={AUTOTASK_POD_FULL_SIZE} element={<FullSizeAutotask />} />
          <Route path={FRESH_SERVICE_FULL_SIZE} element={<FullSizefreshService />} />
          <Route path={KASEYA_POD} element={<KaseyaPod />} />
          <Route path={SYNCRO_POD} element={<SyncroPod />} />
          <Route path={SUPER_OPS_POD} element={<SuperOpsPod />} />
          <Route path={ZENDESK_POD} element={<ZendeskPod />} />
          <Route path={AUTOTASK_POD} element={<AutotaskPod />} />
          <Route path={FRESH_SERVICE_POD} element={<FreshServicePod />} />
          <Route path={MICROSOFT_365_POD} element={<MicrosoftPod />} />
          <Route path={SERVICE_NOW_POD} element={<ServiceNowPod />} />
          <Route path={IMPORT_INTEGRATION_POD} element={<ImportIntegrationPod />} />
        </Route>
        <Route
          path={WEB_EXTENSION_POD}
          element={
            <Suspense fallback={<Loader />}>
              <Layout>
                <WebExtensionPod />
              </Layout>
            </Suspense>
          }
        />
        <Route
          path={AUTOTASK_POD_WIDGET}
          element={
            <Suspense fallback={<Loader />}>
              <Layout>
                <AutotaskPod />
              </Layout>
            </Suspense>
          }
        />
        <Route
          path={FRESH_SERVICE_POD_WIDGET}
          element={
            <Suspense fallback={<Loader />}>
              <Layout>
                <FreshServicePod />
              </Layout>
            </Suspense>
          }
        />
        <Route path={'/*'} element={<Login />} />
      </Routes>
    </BrowserRouter>
  );
};

export default Router;
